import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { OprSession } from '../auth/types/session';
import { formatRolesToString } from '../utils/role';

export function useSetupClarityTracking(locale: string, session?: OprSession) {
  const router = useRouter();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const user = {
    name: session?.user?.proxiedAs?.name ?? session?.user.name,
    zone: session?.user?.proxiedAs?.zone ?? session?.user.zone,
    globalId: session?.user?.proxiedAs?.globalId ?? session?.user.globalId,
    employeeId:
      session?.user?.proxiedAs?.employeeId ?? session?.user.employeeId,
    roles: session?.user?.proxiedAs?.role ?? session?.user.role,
    managedZones: session?.user?.proxiedAs?.zones ?? session?.user.zones,
    band: session?.user?.proxiedAs?.band ?? session?.user.band,
    country: session?.user.country,
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !window.clarity) return;

    function addTag(tag: string, value?: string | string[]) {
      if (!window.clarity) return;

      window.clarity('set', tag, value);
    }

    function initTagConfig(userSession: typeof user) {
      if (!window.clarity) return;

      addTag('employeeId', userSession.employeeId);
      addTag('zone', userSession.zone);
      addTag('band', userSession.band);
      addTag('country', userSession.country);
      addTag('role', formatRolesToString(userSession.roles));
    }

    if (user) {
      initTagConfig(user);
    }
  }, [locale, user]);

  useEffect(() => {
    function trackPageChange(userSession: typeof user, url: string) {
      if (!window.clarity) return;

      window.clarity('identify', userSession.employeeId, null, url);
    }

    if (user) {
      trackPageChange(user, router.pathname);
    }

    router.events.on('routeChangeComplete', trackPageChange);

    return () => {
      router.events.off('routeChangeComplete', trackPageChange);
    };
  }, [router, user]);
}

export function fireEvent(eventName: string) {
  if (typeof window === 'undefined' || !window.clarity) {
    return;
  }

  window?.clarity('event', eventName);
}
